import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react'
import { dealsApi, type ReviewQueueDeal } from '@api/client'
import { formatCurrency, formatDate } from '@utils/format'

// ---- helpers (kept local; mirror ReviewQueueTab) ---------------------------

function assessmentDot(assessment: string | null | undefined): { cls: string; label: string } {
  switch ((assessment || '').toLowerCase()) {
    case 'great': return { cls: 'bg-emerald-500', label: 'Great' }
    case 'good': return { cls: 'bg-green-500', label: 'Good' }
    case 'average': return { cls: 'bg-amber-500', label: 'Average' }
    case 'poor': case 'bad': return { cls: 'bg-red-500', label: 'Poor' }
    default: return { cls: 'bg-gray-500', label: 'No score' }
  }
}

function marginColour(margin: number | null | undefined): string {
  if (margin == null) return 'text-[var(--text-muted)]'
  if (margin >= 70) return 'text-emerald-400'
  if (margin >= 65) return 'text-green-400'
  if (margin >= 60) return 'text-amber-400'
  return 'text-red-400'
}

// Derive a display name from an email local-part, e.g. jacqui.tan@x → "Jacqui Tan".
function nameFromEmail(email: string | null | undefined): string | null {
  if (!email) return null
  return email.split('@')[0].split('.').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
}

// ---- component -------------------------------------------------------------

export function ApprovedTab() {
  const [deals, setDeals] = useState<ReviewQueueDeal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    ;(async () => {
      setLoading(true)
      const res = await dealsApi.listApproved()
      if (!alive) return
      if (res.success && res.data) {
        setDeals(res.data.items)
        setError(null)
      } else {
        setError(res.error || 'Failed to load approved deals')
      }
      setLoading(false)
    })()
    return () => { alive = false }
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading approved deals…
      </div>
    )
  }

  if (error) {
    return <div className="py-8 text-center text-red-400 text-sm">{error}</div>
  }

  if (deals.length === 0) {
    return (
      <div className="py-12 text-center text-[var(--text-muted)]">
        <CheckCircle2 className="mx-auto mb-2 text-emerald-500" size={28} />
        <p className="text-sm">No approved deals yet.</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto border border-[var(--border-color)] rounded-lg">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[var(--bg-tertiary)] text-[var(--text-muted)] text-[11px] uppercase tracking-wide">
            <th className="text-left font-medium px-3 py-2">Customer</th>
            <th className="text-center font-medium px-3 py-2">Assessment</th>
            <th className="text-left font-medium px-3 py-2">Approved</th>
            <th className="text-left font-medium px-3 py-2">Approved by</th>
            <th className="text-right font-medium px-3 py-2">TCV / ARR</th>
            <th className="text-right font-medium px-3 py-2">Margin</th>
            <th className="text-right font-medium px-3 py-2">Impl fee</th>
            <th className="text-left font-medium px-3 py-2">Submitted by</th>
          </tr>
        </thead>
        <tbody>
          {deals.map((d) => {
            const dot = assessmentDot(d.quote?.overall_assessment)
            const feeSynced = !!d.quote?.implementation_fee_from_scope
            const approvedBy = nameFromEmail(d.reviewed_by || d.finalized_by) || '—'
            const submittedBy = d.review_submitted_by_name || d.impl_review_requested_by_name || '—'
            // Whether Implementations validated the fee before final sign-off (the "both
            // teams" path) — surfaced as a small tag next to the impl fee.
            const implValidated = !!d.impl_review_completed_by_name
            return (
              <tr key={d.id} className="border-t border-[var(--border-color)] hover:bg-[var(--bg-tertiary)]/50">
                {/* Customer */}
                <td className="px-3 py-2">
                  <Link to={`/quote?session=${d.id}`} className="text-[var(--text-primary)] font-medium hover:text-primary">
                    {d.customer_name || 'Untitled'}
                  </Link>
                  {d.hubspot_deal_name && (
                    <div className="text-[11px] text-[var(--text-muted)] truncate max-w-[200px]">{d.hubspot_deal_name}</div>
                  )}
                </td>

                {/* Assessment RAG */}
                <td className="px-3 py-2">
                  <div className="flex items-center justify-center" title={dot.label}>
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${dot.cls}`} />
                  </div>
                </td>

                {/* Approved date */}
                <td className="px-3 py-2 text-[var(--text-secondary)] text-[12px] whitespace-nowrap">
                  {d.finalized_at ? formatDate(d.finalized_at) : '—'}
                </td>

                {/* Approved by */}
                <td className="px-3 py-2 text-[var(--text-secondary)] text-[12px]">{approvedBy}</td>

                {/* TCV / ARR */}
                <td className="px-3 py-2 text-right">
                  <div className="text-[var(--text-primary)]">{d.quote?.tcv != null ? formatCurrency(d.quote.tcv) : '—'}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">{d.quote?.arr != null ? `${formatCurrency(d.quote.arr)}/yr` : ''}</div>
                </td>

                {/* Margin */}
                <td className={`px-3 py-2 text-right font-mono ${marginColour(d.quote?.gross_profit_margin)}`}>
                  {d.quote?.gross_profit_margin != null ? `${d.quote.gross_profit_margin.toFixed(1)}%` : '—'}
                </td>

                {/* Impl fee + impl-validated tag */}
                <td className="px-3 py-2 text-right">
                  <div className="text-[var(--text-primary)]">{d.quote?.implementation_fee != null ? formatCurrency(d.quote.implementation_fee) : '—'}</div>
                  {implValidated ? (
                    <div className="text-[10px] inline-flex items-center gap-0.5 text-emerald-400" title={`Impl-validated by ${d.impl_review_completed_by_name}`}>
                      <CheckCircle2 size={10} /> impl-validated
                    </div>
                  ) : (d.quote?.implementation_fee ?? 0) > 0 && !feeSynced ? (
                    <div className="text-[10px] inline-flex items-center gap-0.5 text-[var(--text-muted)]">
                      <AlertTriangle size={10} /> not scope-synced
                    </div>
                  ) : null}
                </td>

                {/* Submitted by */}
                <td className="px-3 py-2 text-[var(--text-secondary)] text-[12px]">{submittedBy}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
